import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'

import { BASE_URL_V3 } from 'config/env'
import {
  camelizeErrorInterceptor,
  camelizeResponseInterceptor,
  decamelizeRequestInterceptor,
  setAuthHeaders,
} from 'repositories/utils/interceptors'

/**
 * `/api/v3` 系エンドポイント用の client。
 *
 * v1 系（`apiClient` / `berthApiClient` 等）との違いは 2 点で、いずれも BE 側の ADR による:
 * - レスポンスは `{ status, data, message }` エンベロープ**なし**の DTO 直返し（ADR-029）。
 *   このため `TruckbookApiResponse<T>` では受けず、DTO の型をそのまま指定する
 * - エラーは RFC 9457 の ProblemDetail（ADR-030）。`error_code` に `ApiErrorCode` の列挙名が入る。
 *   v1 エンベロープ前提の `createTruckbookApiErrorInterceptor` は配線しない。
 *   代わりにエラー時も camelize し、呼び出し側は `errorCode` で読む
 *
 * エラーハンドリングの方針は ADR-009（`docs/adr/009-api-v3-problem-detail-not-normalized.md`）と
 * `.claude/rules/error-handling.md`「/api/v3 のエラーハンドリング」を参照。
 */
// WHY: .env は gitignore 対象で追記漏れが起きやすい。undefined のままだと自分の origin への
// 相対リクエストになり HTML が返るため、「JSON parse 失敗」等の追いにくいエラーになる
if (import.meta.env.DEV && !BASE_URL_V3) {
  console.warn(
    '[apiClientV3] VITE_API_URL_V3 が未設定です。.env.example を参考にローカルの .env へ追記してください。'
  )
}

export const apiClientV3 = axios.create({
  baseURL: BASE_URL_V3,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClientV3.interceptors.request.use((config) => {
  setAuthHeaders(config)
  return decamelizeRequestInterceptor(config)
})

apiClientV3.interceptors.response.use(camelizeResponseInterceptor, camelizeErrorInterceptor)

/**
 * `config.params` は `decamelizeRequestInterceptor` が snake_case に変換するため、
 * クエリは URL に手組みせず `{ params: { tenantId: 1 } }` の形で渡す。
 */
export const fetcherV3 = <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  apiClientV3.get<T>(url, config).then((res) => res.data)
